import { useEffect, useMemo, useState } from "react";
import { useUtils } from "../../../context/UtilsContext";
import { buildStatusIndex } from "./bookingStatus";

/**
 * The booking vocabulary from `GET /get-common-utils`: the `StatusUtils`
 * statuses indexed by normalised title, plus the `weekdays` and
 * `coach_shift_types` ids that `POST /modify-coach-shift` requires.
 *
 * `POST /modify-booking` wants a `booking_status_id`, and a booking row only
 * ever carries the *title* — so something has to hold the mapping. Shifts are
 * the same story: a shift names its weekday by this service's own
 * `Weekdays.id`, never by number, and must carry a `coach_shift_type_id`
 * (only "Booking" exists). All three come from the one endpoint, served by the
 * same FastAPI app that owns bookings (`axiosSubscriptionInstance` and
 * `axiosSubscriptionInstance` share a base URL).
 *
 * Nothing on the Bookings path populates `corporateUtils` today — `BookingsPage`
 * takes `departmentsWithRoles` from `ApiContext`, not from here — so on a cold
 * session this does fetch. `fetchCorporateUtils` serves sessionStorage first, so
 * it costs nothing on a warm one — **except** that a payload cached before the
 * backend added `weekdays` would otherwise be served forever this session, so a
 * cached copy missing them is refetched with `force`.
 *
 * **The fetcher is called only if it is actually a function.** That is not
 * defensiveness for its own sake: every Bookings test mocks `useUtils` as
 * `() => ({ sessionUtils })`, so an unguarded call is `undefined(...)` and would
 * take out the existing suites. With the guard, a partial mock degrades to an
 * empty index, which the UI already has a story for.
 *
 * An empty index means "not configured", never "no statuses exist" — the Set
 * Status options disable themselves and say so, rather than quietly vanishing
 * and reading as a permission rule. Likewise `shiftsReady` false disables
 * shift editing rather than guessing ids.
 *
 * @returns {{
 *   statusIndex: Map<string, { id: string, title: string }>,
 *   ready: boolean,
 *   weekdayIdByNumber: Map<number, string>,
 *   coachShiftTypeId: string|null,
 *   shiftsReady: boolean,
 * }}
 */
export function useBookingStatuses() {
  const { corporateUtils, fetchCorporateUtils } = useUtils() || {};
  const [attempted, setAttempted] = useState(false);

  const bookingStatuses = corporateUtils?.booking_statuses;
  const weekdays = corporateUtils?.weekdays;
  const coachShiftTypes = corporateUtils?.coach_shift_types;

  useEffect(() => {
    if ((bookingStatuses && weekdays) || attempted) return;
    if (typeof fetchCorporateUtils !== "function") {
      setAttempted(true);
      return;
    }
    let cancelled = false;
    // Once per mount at most, whatever the outcome: a payload that comes back
    // without these keys is an answer, and retrying it on every render would
    // be a request loop. A payload already in hand but missing `weekdays` is a
    // stale session copy, so that one bypasses sessionStorage.
    Promise.resolve(fetchCorporateUtils({ force: Boolean(corporateUtils) }))
      .catch(() => null)
      .finally(() => {
        if (!cancelled) setAttempted(true);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingStatuses, weekdays, attempted, fetchCorporateUtils]);

  const statusIndex = useMemo(
    () => buildStatusIndex(bookingStatuses),
    [bookingStatuses]
  );

  const weekdayIdByNumber = useMemo(() => {
    const map = new Map();
    (Array.isArray(weekdays) ? weekdays : []).forEach((day) => {
      const number = Number(day?.day_number);
      if (day?.id && Number.isInteger(number)) map.set(number, day.id);
    });
    return map;
  }, [weekdays]);

  const coachShiftTypeId = useMemo(() => {
    const list = Array.isArray(coachShiftTypes) ? coachShiftTypes : [];
    // "Booking" is the only type today; prefer it by title so a second type
    // added later does not silently become the default.
    const booking = list.find(
      (type) => String(type?.title || "").trim().toLowerCase() === "booking"
    );
    return (booking || list[0])?.id || null;
  }, [coachShiftTypes]);

  return {
    statusIndex,
    /** Whether the vocabulary is usable. False disables the whole action. */
    ready: statusIndex.size > 0,
    weekdayIdByNumber,
    coachShiftTypeId,
    /** All seven weekday ids and a shift type — without them no shift can be written. */
    shiftsReady: weekdayIdByNumber.size === 7 && Boolean(coachShiftTypeId),
  };
}
